/**
 * Swatch math of the kit's colour pickers: a standard palette with tint and
 * shade steps per column, a grayscale row, hex parsing, and an in-memory list
 * of recently used colours. Pure, apart from the recent list.
 */
import { ref, type Ref } from 'vue';

/**
 * The ten standard colours (the Office palette), base row of the grid. `name`
 * is the German label, `key` the kit message under
 * `common.color_picker.colors.<key>`.
 */
export const NX_STANDARD_COLORS: readonly { hex: string; name: string; key: string }[] = [
  { hex: '#C00000', name: 'Dunkelrot', key: 'dark_red' },
  { hex: '#FF0000', name: 'Rot', key: 'red' },
  { hex: '#FFC000', name: 'Orange', key: 'orange' },
  { hex: '#FFFF00', name: 'Gelb', key: 'yellow' },
  { hex: '#92D050', name: 'Hellgrün', key: 'light_green' },
  { hex: '#00B050', name: 'Grün', key: 'green' },
  { hex: '#00B0F0', name: 'Hellblau', key: 'light_blue' },
  { hex: '#0070C0', name: 'Blau', key: 'blue' },
  { hex: '#002060', name: 'Dunkelblau', key: 'dark_blue' },
  { hex: '#7030A0', name: 'Lila', key: 'purple' },
];

/** Tint steps below each base: three lighter, two darker (> 0 toward white). */
export const NX_TINT_STEPS: readonly number[] = [0.8, 0.6, 0.4, -0.25, -0.5];

/** Black to white in ten even steps. */
export const NX_GRAYSCALE_COLORS: readonly string[] = Array.from({ length: 10 }, (_, i) => {
  const c = Math.round((i / 9) * 255)
    .toString(16)
    .toUpperCase()
    .padStart(2, '0');
  return `#${c}${c}${c}`;
});

const HEX = /^#?([0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

/**
 * Normalises a hex colour to `#RRGGBB` (or `#RRGGBBAA` when it has alpha and
 * `allowAlpha` is set): expands short forms, adds the `#`, upper-cases.
 * Returns `null` for anything that is not a hex colour.
 */
export function normalizeHex(input: string | null | undefined, allowAlpha = false): string | null {
  const m = HEX.exec(String(input ?? '').trim());
  if (!m) return null;
  let body = m[1]!.toUpperCase();
  if (body.length <= 4) body = [...body].map((c) => c + c).join('');
  if (body.length === 8 && !allowAlpha) return null;
  if (body.length === 8 && body.endsWith('FF')) body = body.slice(0, 6);
  return `#${body}`;
}

/** Blends `#RRGGBB` toward white (`tint > 0`) or black (`tint < 0`). */
export function tintHex(hex: string, tint: number): string {
  const n = normalizeHex(hex) ?? '#000000';
  const t = Math.max(-1, Math.min(1, tint));
  const ch = (i: number) => parseInt(n.slice(i, i + 2), 16);
  const mix = (c: number) => (t > 0 ? Math.round(c * (1 - t) + 255 * t) : Math.round(c * (1 + t)));
  const out = [1, 3, 5].map((i) =>
    Math.max(0, Math.min(255, mix(ch(i))))
      .toString(16)
      .toUpperCase()
      .padStart(2, '0')
  );
  return `#${out.join('')}`;
}

export interface NxSwatch {
  hex: string;
  /** Accessible name: „Rot“, „Rot, 40 % heller“. */
  label: string;
}

/**
 * The grid, column-major: `grid[col][row]`, row 0 the base colour, then one
 * row per tint step. Labels are German unless `nameOf` names the colour;
 * `lighter`/`darker` are the step templates (`{name}`, `{pct}`).
 */
export function standardColorGrid(
  lighter = '{name}, {pct} % heller',
  darker = '{name}, {pct} % dunkler',
  nameOf: (color: (typeof NX_STANDARD_COLORS)[number]) => string = (color) => color.name
): NxSwatch[][] {
  return NX_STANDARD_COLORS.map((color) => [
    { hex: color.hex, label: nameOf(color) },
    ...NX_TINT_STEPS.map((tint) => ({
      hex: tintHex(color.hex, tint),
      label: (tint > 0 ? lighter : darker)
        .replace('{name}', nameOf(color))
        .replace('{pct}', String(Math.round(Math.abs(tint) * 100))),
    })),
  ]);
}

// Recent colours: one list per key for the whole app session.
const recentLists = new Map<string, Ref<string[]>>();

/**
 * Recently used colours under `key` (most recent first, at most `max`),
 * shared by every picker with the same key for the session.
 */
export function useNxRecentColors(key = 'default', max = 10) {
  let list = recentLists.get(key);
  if (!list) {
    list = ref<string[]>([]);
    recentLists.set(key, list);
  }
  const recent = list;
  function remember(hex: string) {
    const n = normalizeHex(hex, true);
    if (!n) return;
    recent.value = [n, ...recent.value.filter((c) => c !== n)].slice(0, max);
  }
  return { recent, remember };
}
